import { Stack } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { SendMessage } from "@/components/composer/send-message";
import { MonthwiseActivityTable } from "@/components/activity/months-activity";
import {
  getContacts,
  getEmails,
  getGetContactsQueryKey,
  getGetEmailsQueryKey,
  getGetTwilioSettingsQueryKey,
  getTwilioSettings,
  useGetCustomer,
} from "@/models/backend";
import { queryClient } from "@/helpers/tanstack-client";
import z from "zod";

const searchSchema = z.object({
  type: z.enum(["sms", "email"]).default("email"),
});

export const Route = createFileRoute("/_authenticated/_contained/customers/$customerId/compose")({
  loaderDeps: ({ search: { type } }) => ({ type }),
  loader: async ({ params: { customerId }, deps: { type } }) => {
    await Promise.all([
      queryClient.query({
        queryFn: () => getContacts(customerId),
        queryKey: getGetContactsQueryKey(customerId),
        staleTime: "static",
      }),
      // TODO not sure it's required, but this determines the SMS button
      queryClient.query({
        queryFn: () => getTwilioSettings(),
        queryKey: getGetTwilioSettingsQueryKey(),
        staleTime: "static",
      }),
      ...(type === "email"
        ? [
            queryClient.query({
              queryFn: () => getEmails(),
              queryKey: getGetEmailsQueryKey(),
              staleTime: "static",
            }),
          ]
        : []),
    ]);
    return { crumb: "Compose" };
  },
  component: RouteComponent,
  validateSearch: searchSchema,
});

function RouteComponent() {
  const { customerId } = Route.useParams();
  const { type } = Route.useSearch();

  const navigate = Route.useNavigate();
  const { data: customer } = useGetCustomer(customerId);
  const onClose = () => {
    navigate({ to: ".." });
  };
  if (!customer) return null;
  return (
    <Stack spacing={4}>
      <SendMessage
        type={type}
        setType={(type) => navigate({ to: ".", search: { type } })}
        onSent={onClose}
        customer={customer}
      />
      <MonthwiseActivityTable customerId={customer.id} />
    </Stack>
  );
}
